<template>
  <div>
    <div class="toolbar">
      <el-radio-group v-model="platform" size="small" @change="load">
        <el-radio-button label="android">Android</el-radio-button>
        <el-radio-button label="ios">iOS</el-radio-button>
      </el-radio-group>
      <el-button type="primary" icon="el-icon-plus" @click="openEdit()">发布新版本</el-button>
    </div>

    <el-table :data="list" border stripe v-loading="loading">
      <el-table-column prop="versionName" label="版本名称" width="120" />
      <el-table-column prop="versionCode" label="版本号" width="100" />
      <el-table-column prop="updateType" label="包类型" width="90">
        <template slot-scope="{ row }">{{ row.updateType || '-' }}</template>
      </el-table-column>
      <el-table-column prop="forceUpdate" label="强制更新" width="100">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.forceUpdate === 1 ? 'danger' : 'info'">
            {{ row.forceUpdate === 1 ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="enabled" label="状态" width="90">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.enabled === 1 ? 'success' : 'info'">
            {{ row.enabled === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="changelog" label="更新说明" min-width="180" show-overflow-tooltip />
      <el-table-column prop="downloadUrl" label="下载地址" min-width="200" show-overflow-tooltip />
      <el-table-column prop="createdAt" label="发布时间" width="170" />
      <el-table-column label="操作" width="140" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openEdit(row)">编辑</el-button>
          <el-button type="text" style="color:#f56c6c" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="form.id ? '编辑版本' : '发布新版本'"
      :visible.sync="visible"
      width="640px"
      append-to-body
    >
      <el-form :model="form" label-width="110px">
        <el-form-item label="平台" required>
          <el-radio-group v-model="form.platform" :disabled="!!form.id">
            <el-radio label="android">Android</el-radio>
            <el-radio label="ios">iOS</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="版本名称" required>
          <el-input v-model="form.versionName" placeholder="如 1.0.1，需与 App manifest.versionName 对应" />
        </el-form-item>
        <el-form-item label="版本号" required>
          <el-input-number v-model="form.versionCode" :min="1" :step="1" />
          <span class="hint">整数，越大越新；需大于当前 App 的 versionCode</span>
        </el-form-item>
        <el-form-item v-if="form.platform === 'android'" label="安装包">
          <el-upload
            action="#"
            :http-request="doUpload"
            :show-file-list="false"
            :before-upload="beforeUpload"
            accept=".apk,.wgt"
          >
            <el-button size="small" type="primary" plain :loading="uploading">
              {{ uploading ? ('上传中 ' + uploadPercent + '%') : '上传 apk/wgt' }}
            </el-button>
          </el-upload>
          <div v-if="form.attachmentId" class="hint ok">已上传附件 #{{ form.attachmentId }}（{{ form.updateType || '-' }}）</div>
        </el-form-item>
        <el-form-item :label="form.platform === 'ios' ? '商店链接' : '下载地址'">
          <el-input
            v-model="form.downloadUrl"
            type="textarea"
            :rows="2"
            :placeholder="form.platform === 'ios'
              ? 'App Store 地址，可手填'
              : '可上传安装包自动生成；也可手填外链'"
          />
        </el-form-item>
        <el-form-item v-if="form.platform === 'android'" label="包类型">
          <el-radio-group v-model="form.updateType">
            <el-radio label="apk">apk 整包</el-radio>
            <el-radio label="wgt">wgt 热更新</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="强制更新">
          <el-switch v-model="form.forceUpdate" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="更新说明">
          <el-input v-model="form.changelog" type="textarea" :rows="4" placeholder="展示给用户的更新内容" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listAppVersions, saveAppVersion, deleteAppVersion } from '../../api/sys'
import { uploadFileDirect } from '../../utils/ossUpload'

export default {
  name: 'SysAppVersion',
  data() {
    return {
      platform: 'android',
      list: [],
      loading: false,
      visible: false,
      saving: false,
      uploading: false,
      uploadPercent: 0,
      form: {}
    }
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await listAppVersions({ platform: this.platform })
        this.list = res.data || []
      } finally {
        this.loading = false
      }
    },
    openEdit(row) {
      if (row && row.id) {
        this.form = {
          id: row.id,
          platform: row.platform,
          versionName: row.versionName,
          versionCode: row.versionCode,
          forceUpdate: row.forceUpdate == null ? 0 : row.forceUpdate,
          updateType: row.updateType || 'apk',
          downloadUrl: row.downloadUrl || '',
          attachmentId: row.attachmentId || null,
          changelog: row.changelog || '',
          enabled: row.enabled == null ? 1 : row.enabled
        }
      } else {
        this.form = {
          id: null,
          platform: this.platform,
          versionName: '',
          versionCode: 101,
          forceUpdate: 0,
          updateType: 'apk',
          downloadUrl: '',
          attachmentId: null,
          changelog: '',
          enabled: 1
        }
      }
      this.visible = true
    },
    beforeUpload(file) {
      const name = (file.name || '').toLowerCase()
      if (!name.endsWith('.apk') && !name.endsWith('.wgt')) {
        this.$message.warning('请上传 .apk 或 .wgt 文件')
        return false
      }
      if (file.size > 200 * 1024 * 1024) {
        this.$message.warning('安装包请小于 200MB')
        return false
      }
      return true
    },
    async doUpload({ file }) {
      this.uploading = true
      this.uploadPercent = 0
      try {
        const res = await uploadFileDirect(file, (p) => { this.uploadPercent = p })
        this.onUploadOk(res)
      } catch (e) {
        this.onUploadErr(e)
      } finally {
        this.uploading = false
        this.uploadPercent = 0
      }
    },
    onUploadOk(res) {
      const data = (res && res.data) || res || {}
      if (res && res.code && res.code !== 200) {
        this.$message.error(res.message || '上传失败')
        return
      }
      const id = data.id
      const name = (data.fileName || data.originalName || '').toLowerCase()
      this.form.attachmentId = id
      // 优先用 OSS 公网地址（自定义域名）；否则回退本站代理
      this.form.downloadUrl = data.url || (id ? ('/api/files/' + id + '/download') : '')
      if (name.endsWith('.wgt')) this.form.updateType = 'wgt'
      else this.form.updateType = 'apk'
      this.$message.success('上传成功')
    },
    onUploadErr(e) {
      const msg = (e && e.message) || '上传失败'
      this.$message.error(msg)
    },
    async save() {
      if (!this.form.versionName) return this.$message.warning('请填写版本名称')
      if (!this.form.versionCode) return this.$message.warning('请填写版本号')
      if (!this.form.downloadUrl && !this.form.attachmentId) {
        return this.$message.warning(this.form.platform === 'ios' ? '请填写商店链接' : '请上传安装包或填写下载地址')
      }
      this.saving = true
      try {
        await saveAppVersion({ ...this.form })
        this.$message.success('已保存')
        this.visible = false
        this.platform = this.form.platform
        this.load()
      } finally {
        this.saving = false
      }
    },
    remove(row) {
      this.$confirm('确定删除该版本记录？', '提示', { type: 'warning' })
        .then(async () => {
          await deleteAppVersion(row.id)
          this.$message.success('已删除')
          this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.hint {
  margin-left: 12px;
  color: #94a3b8;
  font-size: 12px;
}
.hint.ok {
  display: block;
  margin: 8px 0 0;
  color: #16a34a;
}
</style>
